import { useEffect, useRef } from "react"
import { useAuth0 } from "@auth0/auth0-react"
import { useApiWithAuth } from "../hooks/useAuth.js"
import i18n from "./config.js"

type BackendLocale = "pt" | "en"

function toBackendLocale(lng: string | undefined): BackendLocale {
  return lng?.slice(0, 2) === "en" ? "en" : "pt"
}

/**
 * Keeps the backend's per-user notification language (User.locale) in sync with
 * the UI language: once per authenticated session and on every language change.
 * Failures are silent; the backend falls back to Portuguese.
 */
export function useSyncLocaleToBackend(): void {
  const { isAuthenticated } = useAuth0()
  const { apiCall, isPermissionsLoading } = useApiWithAuth()
  const apiRef = useRef(apiCall)
  apiRef.current = apiCall
  const lastSyncedRef = useRef<BackendLocale | null>(null)

  useEffect(() => {
    if (!isAuthenticated) {
      lastSyncedRef.current = null
      return
    }
    if (isPermissionsLoading) return

    const sync = async (lng: string | undefined) => {
      const locale = toBackendLocale(lng)
      if (lastSyncedRef.current === locale) return
      lastSyncedRef.current = locale
      try {
        await apiRef.current("/api/user/locale", {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ locale }),
        })
      } catch (error) {
        lastSyncedRef.current = null
        console.warn("[i18n] failed to sync locale to backend:", error)
      }
    }

    void sync(i18n.resolvedLanguage)
    const onChange = (lng: string) => void sync(lng)
    i18n.on("languageChanged", onChange)
    return () => {
      i18n.off("languageChanged", onChange)
    }
  }, [isAuthenticated, isPermissionsLoading])
}
